import { useEffect, useState } from "react";
import { DollarSign, Euro, PoundSterling, Coins, ArrowDownUp } from "lucide-react";
import { api } from "../lib/api";
import { Input } from "./ui/input";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "./ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";

const ICONS = { USD: DollarSign, EUR: Euro, GBP: PoundSterling, ALTIN: Coins };
const LABELS = { USD: "USD", EUR: "EUR", GBP: "GBP", ALTIN: "Gram Altın" };

const fmtNum = (v, digits = 2) =>
  new Intl.NumberFormat("tr-TR", { minimumFractionDigits: digits, maximumFractionDigits: digits }).format(v);

export default function RatesStrip() {
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(false);
  const [currency, setCurrency] = useState("USD");
  const [amount, setAmount] = useState("");
  const [direction, setDirection] = useState("try"); // "try": TL -> döviz, "fx": döviz -> TL

  useEffect(() => {
    api.get("/rates").then((r) => setData(r.data)).catch(() => {});
  }, []);

  if (!data) return null;

  const rate = data.rates.find((r) => r.base === currency)?.rate || 0;
  const num = parseFloat(String(amount).replace(",", ".")) || 0;
  const result = direction === "try" ? (rate ? num / rate : 0) : num * rate;
  const unitLabel = LABELS[currency];

  const openConverter = (base) => {
    setCurrency(base);
    setOpen(true);
  };

  return (
    <>
      <div className="flex flex-wrap items-center gap-2" data-testid="rates-strip">
        {data.rates.map((r) => {
          const Icon = ICONS[r.base] || Coins;
          return (
            <button key={r.base} data-testid={`rate-${r.base.toLowerCase()}`} onClick={() => openConverter(r.base)}
              title="Döviz çeviriciyi aç"
              className="flex items-center gap-2 bg-card border border-border rounded-full pl-2 pr-3 py-1.5 hover:border-primary/60 transition-colors">
              <div className="w-6 h-6 rounded-full bg-primary/15 flex items-center justify-center">
                <Icon className="w-3.5 h-3.5 text-primary" />
              </div>
              <span className="text-xs text-muted-foreground">{LABELS[r.base] || r.base}</span>
              <span className="text-xs font-semibold">{fmtNum(r.rate)} ₺</span>
            </button>
          );
        })}
        {data.stale && <span className="text-xs text-muted-foreground">(son bilinen kur)</span>}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="converter-dialog" className="bg-card border-border sm:max-w-sm">
          <DialogHeader>
            <DialogTitle className="font-heading">Döviz Çevirici</DialogTitle>
            <DialogDescription className="sr-only">TL tutarını dövize veya dövizi TL'ye çevirin</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <div className="flex-1 space-y-1">
                <p className="text-xs text-muted-foreground">{direction === "try" ? "Türk Lirası (₺)" : unitLabel}</p>
                <Input data-testid="converter-amount-input" type="number" min="0" step="0.01" autoFocus
                  placeholder="Tutar girin" value={amount} onChange={(e) => setAmount(e.target.value)} />
              </div>
              <button data-testid="converter-swap-button" onClick={() => setDirection(direction === "try" ? "fx" : "try")}
                title="Yönü değiştir"
                className="mt-5 p-2.5 rounded-full bg-primary/15 text-primary hover:bg-primary/25 transition-colors shrink-0">
                <ArrowDownUp className="w-4 h-4" />
              </button>
              <div className="flex-1 space-y-1">
                <p className="text-xs text-muted-foreground">Döviz</p>
                <Select value={currency} onValueChange={setCurrency}>
                  <SelectTrigger data-testid="converter-currency-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-card border-border">
                    {data.rates.map((r) => (
                      <SelectItem key={r.base} value={r.base}>{LABELS[r.base] || r.base}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="bg-secondary/40 border border-border rounded-xl p-4 text-center">
              <p className="text-xs text-muted-foreground mb-1">
                {num > 0
                  ? direction === "try"
                    ? `${fmtNum(num)} ₺ =`
                    : `${fmtNum(num, 4)} ${unitLabel} =`
                  : "Sonuç"}
              </p>
              <p data-testid="converter-result" className="font-heading text-2xl font-bold text-primary">
                {direction === "try" ? `${fmtNum(result, 4)} ${unitLabel}` : `${fmtNum(result)} ₺`}
              </p>
            </div>

            <p className="text-xs text-muted-foreground text-center">
              1 {unitLabel} = {fmtNum(rate)} ₺{data.stale ? " (son bilinen kur)" : ""}
            </p>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
